import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  ACTIVITY_TYPE_DEFS,
  CALL_DIRECTION_LABELS,
  MEETING_PROVIDER_LABELS,
  MESSAGE_CHANNEL_DEFS,
  PRIORITY_DEFS,
  type ActivityType,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { useTenantProfile } from '../../lib/settings';
import { Icon } from '../../components/Icon';


/*
 * لكلّ نوعٍ لغتُه.
 *
 * «التفاصيل» و«ما دار في التواصل» كانتا لكل شيء: الاجتماع والمكالمة
 * والرسالة سواء — فيقرأ من يكتب محضر اجتماعٍ تلميحًا عن «التواصل».
 * التسميةُ تصف ما يُكتب فعلًا، فيُكتب أدقّ.
 */
const DETAIL_LABELS: Record<ActivityType, string> = {
  TASK: 'وصف المهمة',
  MEETING: 'جدول الأعمال',
  CALL: 'ما دار في المكالمة',
  MAIL: 'نصّ الرسالة',
  NOTE: 'الملاحظة',
};

const DETAIL_HINTS: Record<ActivityType, string> = {
  TASK: 'ما المطلوب بالضبط، وما يلزم لإنجازه',
  MEETING: 'بنود النقاش، ومن يعرض ماذا — ويُكتب المحضر هنا بعد الانعقاد',
  CALL: 'سبب الاتصال، وما اتُّفق عليه، والخطوة التالية',
  MAIL: 'خلاصة ما أُرسل أو وصل — وردُّ العميل إن جاء',
  NOTE: 'ما تريد أن يعرفه من يفتح هذا الملف بعدك',
};
/**
 * تسجيل نشاط على السجل المفتوح.
 *
 * موضعه أعلى سجل التعامل مقصود: تسجيل المكالمة يتم فور انتهائها ومن نفس
 * الشاشة، لا بالانتقال إلى قسم منفصل. هذا هو الفرق العملي بين نظام يُستخدم
 * فعلًا ونظام يُملأ لاحقًا من الذاكرة.
 */
export function ActivityComposer({
  link,
  recordMapsUrl,
  onDone,
  defaultType = 'CALL',
  startOpen = false,
  onOpenTask,
  taskPanel,
  meetingPanel,
  callPanel,
  mailPanel,
  notePanel,
  onTypeChange,
  tabsExtra,
}: {
  link: { leadId?: string; accountId?: string; dealId?: string; contactId?: string };
  /** موقع العميل المسجَّل في ملفّه — يملأ خريطة الاجتماع الحضوريّ. */
  recordMapsUrl?: string | null;
  onDone: () => void;
  /** النوع المُنتقى مسبقًا — يفتحه إجراءٌ سريع قصده بعينه. */
  defaultType?: ActivityType;
  /**
   * يفتح النموذج فورًا.
   *
   * حين يأتي المستخدم من إجراء اسمه «إضافة ملاحظة» يكون قد اختار أصلًا،
   * فعرض زرٍّ يطلب منه الاختيار ثانيةً خطوةٌ زائدة.
   */
  startOpen?: boolean;
  /**
   * تبويب «مهمة» بلوحة المشاريع لا بالنموذج.
   *
   * حين تُمرَّر، اختيارُ التبويب يعرض المهام السابقة (`taskPanel`) وزرَّ
   * «إضافة مهمة» الذي يزرعها فورًا في مشروع «مهام إدارة العملاء
   * المحتملين» ويسلّم معرّفها ليُفتح لوحُها الجانبي — حيث تُسمّى ويُحدَّد
   * موعدُها كأي مهمة مشروع. والمسؤول منشئُها ابتداءً.
   */
  onOpenTask?: (agencyTaskId: string) => void;
  /** قائمة المهام السابقة — يعرضها تبويب المهمة فوق زرّ الإضافة. */
  taskPanel?: React.ReactNode;
  /**
   * سجلّ الاجتماعات — يعرضه تبويب الاجتماع تحت زرّ «إضافة اجتماع»،
   * والنموذج الكامل لا يظهر إلا بالزرّ. كنمط تبويب المهمة سواء.
   */
  meetingPanel?: React.ReactNode;
  /** سجلّ المكالمات — تبويب المكالمة على النمط نفسه: زرٌّ فسجلّ. */
  callPanel?: React.ReactNode;
  /** سجلّ الرسائل البريدية — تبويب الرسالة كذلك: زرٌّ فسجلّ. */
  mailPanel?: React.ReactNode;
  /** سجلّ الملاحظات — تبويب الملاحظة على النمط نفسه، بتاريخ كلٍّ وساعتها. */
  notePanel?: React.ReactNode;
  /** يُبلغ الأب بالتبويب المُنتقى — ليُظهر شريطَ ملاحظاتِ ذلك الجانب. */
  onTypeChange?: (t: ActivityType) => void;
  /** عنصرٌ يملأ فراغ سطر التبويبات — كميتر التصنيف في ملف العميل. */
  tabsExtra?: React.ReactNode;
}) {
  const [type, setType] = useState<ActivityType>(defaultType);
  const [open, setOpen] = useState(startOpen);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [priority, setPriority] = useState('NORMAL');
  const [callDirection, setCallDirection] = useState('OUTBOUND');
  const [meetingProvider, setMeetingProvider] = useState('ONSITE');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [durationMin, setDurationMin] = useState('');
  const [location, setLocation] = useState('');
  /* موقع الانعقاد على الخريطة — يُملأ من ملف العميل ويقبل التعديل. */
  const [mapsUrl, setMapsUrl] = useState('');
  const [attendeesText, setAttendeesText] = useState('');
  const [messageChannel, setMessageChannel] = useState('EMAIL');
  const [noteAbout, setNoteAbout] = useState('');

  /*
   * رابط الاجتماع يتولّد من الإعدادات لا يُنسخ يدويًّا.
   *
   * حين يُضبط في «الإعدادات ← الاجتماعات» مزوّدٌ افتراضيّ وغرفةٌ دائمة،
   * يمتلئ المزوّد والرابط تلقائيًّا مع فتح نموذج الاجتماع — ويبقى قابلًا
   * للتبديل لمن يجتمع حضوريًّا هذه المرة.
   */
  const profile = useTenantProfile();
  const settings = profile.data?.settings;
  useEffect(() => {
    if (type !== 'MEETING' || !settings) return;
    setMeetingProvider((prev) =>
      prev === 'ONSITE' && settings.defaultMeetingProvider
        ? (settings.defaultMeetingProvider as string)
        : prev,
    );
    setDurationMin((prev) => prev || String(settings.defaultMeetingMinutes ?? 30));
  }, [type, settings]);

  const online = meetingProvider !== 'ONSITE' && meetingProvider !== 'PHONE';
  const isJitsi = meetingProvider === 'JITSI';

  /*
   * الحضوريّ يرث موقع العميل.
   *
   * من يذهب إلى عميلٍ يحتاج ملاحةً لا عنوانًا يكتبه، والموقع مسجَّلٌ في
   * ملفّه أصلًا — فيُملأ عند اختيار «حضوري» ويبقى قابلًا للتبديل حين
   * يكون اللقاء في مكانٍ آخر.
   */
  useEffect(() => {
    if (type !== 'MEETING' || online) return;
    setMapsUrl((prev) => prev || (recordMapsUrl ?? ''));
  }, [type, online, recordMapsUrl]);
  useEffect(() => {
    /* Jitsi يولّد غرفتَه على الخادم، فلا يُملأ برابطِ الغرفةِ الدائمة. */
    if (type !== 'MEETING' || !online || isJitsi) return;
    setMeetingUrl((prev) => prev || (settings?.meetingRoomUrl ?? ''));
  }, [type, online, isJitsi, settings?.meetingRoomUrl]);

  const create = useMutation({
    mutationFn: () =>
      api('/crm/activities', {
        method: 'POST',
        body: {
          ...link,
          type,
          subject: subject.trim(),
          body: body.trim() || undefined,
          /*
           * المهمة تُجدول دائمًا؛ أما التواصل فيُسجَّل بعد وقوعه في الغالب.
           * والملاحظة منجَزةٌ بحكم كتابتها: لا تُجدول ولا تُنتظر.
           */
          /*
           * ما يُنشأ يُجدول؛ والإنجاز فعلٌ لاحقٌ بزرّه.
           *
           * كان في النموذج مربّع «تمّ بالفعل» مؤشَّرًا سلفًا، فيُولد الاجتماع
           * والمكالمة والرسالة «مكتملةً» قبل أن تقع — ومن نسي رفعه سجّل
           * إنجازًا لم يحدث. والملاحظة وحدها منجَزةٌ بحكم كتابتها.
           */
          status: type === 'NOTE' ? 'DONE' : 'PLANNED',
          priority,
          dueAt: dueAt || undefined,
          callDirection: type === 'CALL' ? callDirection : undefined,

          messageChannel: type === 'MAIL' ? messageChannel : undefined,
          noteAbout: type === 'NOTE' && noteAbout ? noteAbout : undefined,
          meetingProvider: type === 'MEETING' ? meetingProvider : undefined,
          /* Jitsi يولّد غرفتَه على الخادم، فلا يُرسَل رابطٌ يدويّ. */
          meetingUrl:
            type === 'MEETING' && online && !isJitsi && meetingUrl.trim()
              ? meetingUrl.trim()
              : undefined,
          durationMin:
            type === 'MEETING' && Number(durationMin) > 0 ? Number(durationMin) : undefined,
          location:
            type === 'MEETING' && !online && location.trim() ? location.trim() : undefined,
          mapsUrl:
            type === 'MEETING' && !online && mapsUrl.trim() ? mapsUrl.trim() : undefined,
          attendees:
            type === 'MEETING'
              ? attendeesText
                  .split(/[،,]/)
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .slice(0, 50)
              : undefined,
        },
      }),
    onSuccess: () => {
      setSubject('');
      setBody('');
      setDueAt('');
      setOpen(false);
      onDone();
    },
  });

  const canSave = subject.trim().length >= 2 && !create.isPending;

  /* إنشاءٌ فوري لمهمةٍ تُفتح في لوحها — تُسمّى هناك لا في نموذجٍ هنا. */
  const quickTask = useMutation({
    mutationFn: () =>
      api<{ agencyTaskId: string | null }>('/crm/activities', {
        method: 'POST',
        body: { ...link, type: 'TASK', status: 'PLANNED', subject: 'مهمة جديدة' },
      }),
    onSuccess: (res) => {
      onDone();
      if (res.agencyTaskId) onOpenTask?.(res.agencyTaskId);
    },
  });

  return (
    <div>
      <div className="tabs" role="tablist" aria-label="نوع النشاط">
        {ACTIVITY_TYPE_DEFS.map((t) => (
          <button
            key={t.key}
            className="tab"
            role="tab"
            aria-selected={type === t.key}
            onClick={() => {
              setType(t.key);
              onTypeChange?.(t.key);
              /* تبويبٌ له سجلّ يبدأ مغلقَ النموذج — زرّ الإضافة يفتحه. */
              setOpen(
                !(
                  (t.key === 'MEETING' && meetingPanel) ||
                  (t.key === 'CALL' && callPanel) ||
                  (t.key === 'MAIL' && mailPanel) ||
                  (t.key === 'NOTE' && notePanel)
                ),
              );
            }}
          >
            {t.label}
          </button>
        ))}
        {tabsExtra}
      </div>

      {type === 'TASK' && onOpenTask ? (
        /*
          تبويب المهمة شاشةٌ لا نموذج: زرُّ الإضافة في الصدر — يزرع
          الجديدة ويفتح لوحها — والمهام السابقة تحته يُفتح لوح أيٍّ منها.
        */
        <div className="col" style={{ gap: 'var(--sp-3)' }}>
          <span>
            <button
              className="btn btn-primary btn-sm"
              disabled={quickTask.isPending}
              onClick={() => quickTask.mutate()}
            >
              <Icon name="plus" size={14} />
              {quickTask.isPending ? 'جارٍ الإنشاء…' : 'إضافة مهمة'}
            </button>
          </span>
          {taskPanel}
        </div>
      ) : type === 'MEETING' && meetingPanel && !open ? (
        /* زرّ الإضافة في الصدر — وسجلّ اجتماعات هذا العميل تحته. */
        <div className="col" style={{ gap: 'var(--sp-3)' }}>
          <span>
            <button className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>
              <Icon name="plus" size={14} />
              إضافة اجتماع
            </button>
          </span>
          {meetingPanel}
        </div>
      ) : type === 'CALL' && callPanel && !open ? (
        /* زرّ التسجيل في الصدر — وسجلّ مكالمات هذا العميل تحته. */
        <div className="col" style={{ gap: 'var(--sp-3)' }}>
          <span>
            <button className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>
              <Icon name="plus" size={14} />
              إضافة مكالمة
            </button>
          </span>
          {callPanel}
        </div>
      ) : type === 'MAIL' && mailPanel && !open ? (
        /* زرّ الإضافة في الصدر — وسجلّ رسائل هذا العميل تحته. */
        <div className="col" style={{ gap: 'var(--sp-3)' }}>
          <span>
            <button className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>
              <Icon name="plus" size={14} />
              إضافة رسالة
            </button>
          </span>
          {mailPanel}
        </div>
      ) : type === 'NOTE' && notePanel && !open ? (
        /* زرّ الإضافة في الصدر — وملاحظات هذا العميل مؤرَّخةً تحته. */
        <div className="col" style={{ gap: 'var(--sp-3)' }}>
          <span>
            <button className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>
              <Icon name="plus" size={14} />
              إضافة ملاحظة
            </button>
          </span>
          {notePanel}
        </div>
      ) : !open ? (
        <button className="btn" onClick={() => setOpen(true)}>
          <Icon name="plus" size={15} />
          تسجيل {ACTIVITY_TYPE_DEFS.find((t) => t.key === type)?.label}
        </button>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) create.mutate();
          }}
          noValidate
        >
          {create.isError && (
            <div className="alert alert-danger mb-4">
              {create.error instanceof Error ? create.error.message : 'تعذّر الحفظ.'}
            </div>
          )}

          <div className="col">
            <div className="field">
              <label className="label req" htmlFor="a-subject">
                الموضوع
              </label>
              <input
                id="a-subject"
                className="input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={
                  type === 'CALL'
                    ? 'مكالمة متابعة'
                    : type === 'MEETING'
                      ? 'اجتماع عرض الخدمات'
                      : type === 'MAIL'
                        ? 'إرسال عرض السعر'
                        : 'إعداد المستندات'
                }
                autoFocus
              />
            </div>

            <div className="form-grid">
              {type === 'MAIL' && (
                <div className="field">
                  <label className="label" htmlFor="a-channel">
                    القناة
                  </label>
                  <select
                    id="a-channel"
                    className="select"
                    value={messageChannel}
                    onChange={(e) => setMessageChannel(e.target.value)}
                  >
                    {MESSAGE_CHANNEL_DEFS.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  {/* كل قناة تظهر عند أهلها — من يجدول يعرف أين سيجدها المرسِل. */}
                  <span className="tiny mute">
                    تظهر للإرسال في:{' '}
                    {MESSAGE_CHANNEL_DEFS.find((c) => c.key === messageChannel)?.home}
                  </span>
                </div>
              )}

              {type === 'CALL' && (
                <>
                  <div className="field">
                    <label className="label" htmlFor="a-dir">
                      الاتجاه
                    </label>
                    <select
                      id="a-dir"
                      className="select"
                      value={callDirection}
                      onChange={(e) => setCallDirection(e.target.value)}
                    >
                      {Object.entries(CALL_DIRECTION_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {type === 'MEETING' && (
                <>
                  <div className="field">
                    <label className="label" htmlFor="a-provider">
                      مكان الاجتماع
                    </label>
                    <select
                      id="a-provider"
                      className="select"
                      value={meetingProvider}
                      onChange={(e) => setMeetingProvider(e.target.value)}
                    >
                      {/*
                        «هاتفي» ليس اجتماعًا بل مكالمة — وله تبويبه بسجلّه
                        واتجاهه ونتيجته. وجودُه هنا كان يشتّت السجلّين.
                      */}
                      {Object.entries(MEETING_PROVIDER_LABELS)
                        .filter(([k]) => k !== 'PHONE')
                        .map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                    </select>
                  </div>

                  {isJitsi ? (
                    <div className="field">
                      <span className="label">رابط الاجتماع</span>
                      <div className="tiny mute" style={{ lineHeight: 1.7 }}>
                        تُولَّد غرفةُ Jitsi تلقائيًّا وتُفتح داخلَ النظام. المضيفُ يتحكّمُ في كلِّ
                        شيء، ويمكنه تسجيلُ الاجتماعِ فيُلحَق بالقائمةِ للمشاهدة.
                      </div>
                    </div>
                  ) : online ? (
                    <div className="field">
                      <label className="label" htmlFor="a-murl">
                        رابط الاجتماع
                      </label>
                      <input
                        id="a-murl"
                        className="input num"
                        dir="ltr"
                        value={meetingUrl}
                        onChange={(e) => setMeetingUrl(e.target.value)}
                        placeholder="https://…"
                      />
                      <span className="tiny mute">
                        {settings?.meetingRoomUrl
                          ? 'مُلئ من غرفة الاجتماعات الدائمة في الإعدادات — بدّله إن أردت.'
                          : 'اضبط غرفةً دائمة في «الإعدادات ← الاجتماعات» ليمتلئ تلقائيًّا.'}
                      </span>
                    </div>
                  ) : (
                    /* الحضوري له عنوانٌ يُذهب إليه وموقعٌ يُفتح بالملاحة. */
                    <>
                      <div className="field">
                        <label className="label" htmlFor="a-loc">
                          مكان الانعقاد
                        </label>
                        <input
                          id="a-loc"
                          className="input"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          placeholder="مقرّ الشركة — قاعة الاجتماعات، أو عنوان العميل…"
                        />
                      </div>

                      <div className="field">
                        <label className="label" htmlFor="a-maps">
                          الموقع على الخريطة
                        </label>
                        <input
                          id="a-maps"
                          className="input num"
                          dir="ltr"
                          value={mapsUrl}
                          onChange={(e) => setMapsUrl(e.target.value)}
                          placeholder="https://maps.google.com/…"
                        />
                        <span className="tiny mute">
                          {recordMapsUrl
                            ? 'مُلئ من موقع العميل المسجَّل في ملفّه — بدّله إن كان اللقاء في مكانٍ آخر.'
                            : 'لا موقع مسجَّل في ملف العميل — الصقه هنا ليصير زرَّ ملاحةٍ بعد الحفظ.'}
                        </span>
                      </div>
                    </>
                  )}

                  <div className="field">
                    <label className="label" htmlFor="a-dur">
                      المدة
                    </label>
                    <select
                      id="a-dur"
                      className="select"
                      value={durationMin}
                      onChange={(e) => setDurationMin(e.target.value)}
                    >
                      {['15', '30', '45', '60', '90', '120'].map((m) => (
                        <option key={m} value={m}>
                          {m} دقيقة
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="field span-2">
                    <label className="label" htmlFor="a-att">
                      الحضور
                    </label>
                    <input
                      id="a-att"
                      className="input"
                      value={attendeesText}
                      onChange={(e) => setAttendeesText(e.target.value)}
                      placeholder="أسماء أو عناوين بريد، مفصولةً بفواصل"
                    />
                    <span className="tiny mute">
                      يظهرون في تفاصيل الاجتماع — والدعوات تُرسَل من بريدك بعد الحفظ.
                    </span>
                  </div>
                </>
              )}

              {/*
                تصنيف الملاحظة: أيَّ جانبٍ من الملف تخصّ. به تظهر شريطَ
                تنبيهٍ في تبويبها — فمن يفتح الرسائل يرى ما قيل عن الرسائل.
              */}
              {type === 'NOTE' && (
                <div className="field">
                  <label className="label" htmlFor="a-about">
                    تخصّ
                  </label>
                  <select
                    id="a-about"
                    className="select"
                    value={noteAbout}
                    onChange={(e) => setNoteAbout(e.target.value)}
                  >
                    <option value="">عامّة — الملف كلّه</option>
                    {ACTIVITY_TYPE_DEFS.filter((t) => t.key !== 'NOTE').map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <span className="tiny mute">
                    تظهر تنبيهًا أعلى تبويب {noteAbout ? ACTIVITY_TYPE_DEFS.find((t) => t.key === noteAbout)?.plural : 'الجانب الذي تخصّه'}.
                  </span>
                </div>
              )}

              {/*
                الأولوية تُحدَّد عند الإنشاء لكل نوع — ما يستعجل يُعرف من
                لحظته لا بعد أن يتأخّر. وهي هنا وحدها: بعد الإنشاء تصير
                حكمًا مسجَّلًا لا يُبدَّل بأثرٍ رجعي.
              */}
              <div className="field">
                <label className="label" htmlFor="a-prio">
                  الأولوية
                </label>
                <select
                  id="a-prio"
                  className="select"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  {PRIORITY_DEFS.map((d) => (
                    <option key={d.key} value={d.key}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* الملاحظة لا موعد لها: تُسجَّل وقد وقعت. */}
              {type !== 'NOTE' && (
                <div className="field">
                  <label className="label" htmlFor="a-due">
                    {type === 'TASK' ? 'موعد الاستحقاق' : 'الموعد'}
                  </label>
                  <input
                    id="a-due"
                    className="input"
                    type="datetime-local"
                    value={dueAt}
                    onChange={(e) => setDueAt(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="field">
              <label className="label" htmlFor="a-body">
                {DETAIL_LABELS[type]}
              </label>
              <textarea
                id="a-body"
                className="textarea"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={DETAIL_HINTS[type]}
              />
            </div>

            <div className="row">
              <button className="btn btn-primary" type="submit" disabled={!canSave}>
                {create.isPending ? 'جارٍ الحفظ' : 'حفظ'}
              </button>
              <button className="btn" type="button" onClick={() => setOpen(false)}>
                إلغاء
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
